/**
 * Root application component
 * Configures providers and routing
 */
import React, { useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { CssBaseline } from '@mui/material';
import { queryClient } from '@core/config/react-query.config';
import { ThemeContextProvider } from '@core/contexts';
import { NotificationProvider } from '@components/common/NotificationProvider/NotificationProvider';
import { ScrollToTop } from '@components/common';
import { HomeSectionHashHandler } from '@components/common/HomeSectionHashHandler/HomeSectionHashHandler';
import { AISearchPanelProvider } from '@hooks/useAISearchPanel';
import { CopilotStudioProvider } from '@hooks/useCopilotStudio';
import { CopilotAppShell } from '@components/chatbot/copilot';
import { initializeInterceptors } from '@services/api/interceptors';
import { initializeRetryInterceptor } from '@services/api/retry-interceptor';
import AppRoutes from '@routes/AppRoutes';

/**
 * Root application component
 * @returns JSX Element
 */
const App: React.FC = () => {
  /**
   * Initialize axios interceptors on app mount
   * Note: Retry interceptor must be initialized AFTER main interceptors
   * to ensure proper execution order (interceptors execute in reverse registration order)
   */
  useEffect(() => {
    initializeInterceptors();
    // Initialize retry interceptor if enabled (must be after main interceptors)
    // This ensures retry runs before error handler (interceptors execute in reverse order)
    if (window._env_.AIPORTAL_ENABLE_RETRY !== 'false') {
      initializeRetryInterceptor();
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeContextProvider>
        <CssBaseline />
        <NotificationProvider>
          <BrowserRouter>
            <ScrollToTop />
            <HomeSectionHashHandler />
            <CopilotStudioProvider>
              <AISearchPanelProvider>
                <AppRoutes />
                <CopilotAppShell />
              </AISearchPanelProvider>
            </CopilotStudioProvider>
          </BrowserRouter>
        </NotificationProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </ThemeContextProvider>
    </QueryClientProvider>
  );
};

export default App;
